/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import {
  ICondition,
  IMedicationRequest,
  IObservation,
  IPatient,
  IProcedure,
} from '@ahryman40k/ts-fhir-types/lib/R4/Resource';

import { FhirWidgetBase, FhirPredefinedWidget, GroupedObservationType, HtmlFhirInstance } from '../algorithm/types';
import {
  fetchDiagnosticReportWithObservations,
  fetchDiagnosticReportsOnlyRequired,
  fetchPatient,
  fetchPatientByMRN,
  fetchPredefinedWidgets,
  fetchMedicinesVitals,
  fetchProblemList,
  // fetchPatientMedicalHx,
  fetchPatientSurgicalHx,
  FhirNetworkOrganizationValuesFhirType,
  fetchFhirPatientFromAzure,
  throwIfAborted,
  convertFhirPredefinedWidgetToFhirNetworkPredefinedWidget,
  convertFhirNetworkPredefinedWidgetToFhirPredefinedWidget,
  fixupWidgetId,
} from './fhir_network';
import {
  DiagnosticReportAndObservations,
  fhirWidgetIdToRightPanelWidgetId,
  getPatientMRN,
  updatePatientDataFromDiagnosticReport,
} from './fhir_utils';
import { IDiagnosticReportSimplified } from './fhir_types';
import { PatientContextType } from '../Login/types';
import { RootState } from '../../../app/store';
import { UserState } from '../Login/userSlice';
import { createRandomId, hashCode } from '../utils';
import { listToIdDict } from '../chat/chatSlice';
import pearlApi from '../api/pearlApi';
import { snackNotifications } from '../notification/SnackBarUtils';
import {
  shouldFetchCareEverywhere,
  showPatientMedicines,
  showPatientProblemListAndPastMedicalHx,
} from '../permissions';
import { isPatientChartPaneVisible } from '../chat/components/ThreePaneView/ToggleButtons';
import { updateFromGroupedObservations } from './groupedObservations_utils';
import { azureDownloadKnownAcronyms } from '../Azure/azure_utils';
import axios from 'axios';
import { analyticsTrackCareEverywhereData, CareEverywherePanel, fetchCareEverywhere } from './CareEverywhere/network';

export type FhirPatientDataType = IDiagnosticReportSimplified[] | undefined;

export type ContextualizedWidgetReports = {
  [widgetId: string]: {
    widget: FhirPredefinedWidget | undefined;
    reports: DiagnosticReportAndObservations[];
  };
};

export interface FhirState {
  loadingObservations: boolean;
  loadingPatient: boolean;
  predefinedWidgets?: FhirPredefinedWidget[];
  groupedObservations?: GroupedObservationType[];
  loadingPredefinedWidgets: boolean;
  fhirPatientData: {
    diagnosticReports: FhirPatientDataType;
    medicines: IMedicationRequest[] | undefined;
    vitals: IObservation[] | undefined;
    problemList: ICondition[] | undefined;
    medicalHx: ICondition[] | undefined;
    surgicalHx: IProcedure[] | undefined;
    careEverywhere?: CareEverywherePanel[];
  };
  patientContext?: PatientContextType;
  isFhirVisible?: boolean;
  isFhirFilterVisible?: boolean;
  /* id is a random number we create for each RenderedHTML, so that we can clear up when that rendered html is removed.
     We can't use pageId, because a single page might have multiple rendered html (e.g. in tooltips, buttons, etc
      */
  listOfHtmlFhirById: { [id: string]: HtmlFhirInstance[] };
  diagnosticReportObservationsLoading: { [reference: string]: boolean };
  observationCptOrNameLoadingOrLoaded: { [cptOrName: string]: boolean };

  highlightFhir?: {
    documentVersionId: string;
    pageId: string;
    messageIndex: number;
    fhirWidgetIds: string[];
    predefinedWidgetIds: string[] | undefined;
    rightPanelWidgetIds: string[]; // 1-1 mapping with predefinedWidgetIds. E.g. predefinedWidgetIds[0] will be like "CMP" and rightPanelWidgetIds[0] will be like "f1-CMP"
    hoveringOverWidget: boolean; // If true, then the user is hovering over the sidebar widget, so show all the lines from the right to the left
  };
  highlightFhirTop?: number; // This is the top position of the highlighted fhir widget in the sidebar. Necessary for the callout to be positioned correctly
  activeSidebarMessageIndex?: number;
  dateRange?: number;
  loadingKnownAcronyms: boolean;
  knownAcronyms?: Record<string, string>;
}

export const initialState: FhirState = {
  loadingObservations: false,
  loadingPatient: false,
  fhirPatientData: {
    diagnosticReports: undefined,
    medicines: undefined,
    vitals: undefined,
    problemList: undefined,
    medicalHx: undefined,
    surgicalHx: undefined,
  },
  // PRL-2320 TODO - get patientContext from API
  patientContext: {
    // fhirPatientId: 'ebhC.cuW5lKw1ZGhG.EwOyw3',
    //mrn: '20000130034',
    mrn: '',
    fromSmartOnFhir: false,
  },
  isFhirVisible: false,
  listOfHtmlFhirById: {},
  diagnosticReportObservationsLoading: {},
  loadingPredefinedWidgets: false,
  predefinedWidgets: undefined,
  observationCptOrNameLoadingOrLoaded: {},
  highlightFhir: undefined,
  activeSidebarMessageIndex: undefined,
  dateRange: 0, // default selection all
  loadingKnownAcronyms: false,
  knownAcronyms: undefined,
};

export const fetchObservationsForDiagnosticReportThunk = createAsyncThunk<
  { mrn: string; diagnosticReportWithObservations: IDiagnosticReportSimplified | undefined } | undefined,
  { diagnostic_report_id: string | undefined; signal?: AbortSignal }, // reference like Observation/
  { state: { fhir: FhirState } }
>(
  'fhir/fetchReference',
  async ({ diagnostic_report_id, signal }, thunkAPI) => {
    const patientContext = thunkAPI.getState().fhir.patientContext;
    if (!diagnostic_report_id || !patientContext) {
      return;
    }
    const { mrn, server } = patientContext;
    if (!mrn || !server) {
      return;
    }
    const diagnosticReportWithObservations = await fetchDiagnosticReportWithObservations(
      diagnostic_report_id,
      mrn,
      server
    );
    return { mrn, diagnosticReportWithObservations };
  },
  {
    condition: ({ diagnostic_report_id }, { getState }) => {
      const { fhir } = getState();
      // reference is a string like:  Observation/abcde
      if (!diagnostic_report_id) {
        return false;
      }
      if (fhir.patientContext?.azureFilename) {
        return false; // observations will be loaded from the fake fhir server (a file on azure)
      }
      if (!fhir.patientContext?.mrn) {
        console.log(`Unexpected missing MRN: ${JSON.stringify(fhir)}`);
        throw new Error(`Unexpected missing MRN`);
      }
      // Check if already loading
      return !fhir.diagnosticReportObservationsLoading[diagnostic_report_id];
    },
  }
);

export const fetchPredefinedWidgetsThunk = createAsyncThunk<
  | {
      predefined_widgets?: FhirPredefinedWidget[];
      groupedObservations?: GroupedObservationType[];
    }
  | undefined,
  void,
  { state: { fhir: FhirState; user: UserState } }
>(
  'fhir/fetchPredefinedWidgets',
  async (_, thunkAPI) => {
    const orgId = thunkAPI.getState().user.pearlUserInfo?.orgId;
    if (orgId) {
      const predefined_widgets = await fetchPredefinedWidgets(orgId);
      if (!predefined_widgets) return;
      return predefined_widgets;
    }

    return { predefined_widgets: [] };
  },
  {
    condition: (reference, { getState }) =>
      getState().fhir.loadingPredefinedWidgets === false && getState().fhir.predefinedWidgets === undefined,
  }
);

export const updatePredefinedWidgetThunk = createAsyncThunk<
  void, // return type
  FhirPredefinedWidget | undefined, // params
  { state: { fhir: FhirState; user: UserState } }
>('fhir/updatePredefinedWidget', async (predefined_widget, thunkAPI) => {
  // We want to update just a single widget.  We do this in a very crude way.
  // First, refetch the latest list of widgets, just incase someone else editted them.
  // Then modify this specific widget, and then update the entire of widgets.
  if (!predefined_widget) return;
  // Call updateMultiplePredefinedWidgetsThunk
  await thunkAPI
    .dispatch(updateMultiplePredefinedWidgetsThunk({ data: [predefined_widget], doReplace: false }))
    .unwrap();
});

export const updateMultiplePredefinedWidgetsThunk = createAsyncThunk<
  { num_added: number; num_changed: number; num_unchanged: number } | undefined, // return type
  {
    data: FhirPredefinedWidget[];
    doReplace: boolean; // If true, replace the list; otherwise update or add the supplied widgets
    doMerge?: boolean; // If true, the data is just a partial update of the existing widgets, so merge them instead of replacing. Cannot have doReplace and doMerge both true
    doDryRun?: boolean; // If true, just return the number of widgets that would be added/changed, without actually updating the store
  }, // params
  { state: { fhir: FhirState; user: UserState } }
>('fhir/updateMultiplePredefinedWidgets', async (props, thunkAPI) => {
  if (props.doReplace && props.doMerge) {
    throw new Error('Cannot have both doReplace and doMerge set to true');
  }
  const new_predefined_widgets = props.doMerge
    ? props.data
    : props.data
        .filter((w) => w.name)
        .map((w) => {
          // Give it a random ID if not set
          w.id ||= createRandomId();
          return w;
        });
  if (new_predefined_widgets.length === 0) return;

  const orgId = thunkAPI.getState().user.pearlUserInfo?.orgId;
  if (!orgId) {
    throw new Error('You do not have an orgId?');
  }
  const predefined_widgets: FhirPredefinedWidget[] | undefined = props.doReplace
    ? new_predefined_widgets
    : (await fetchPredefinedWidgets(orgId))?.predefined_widgets;
  if (!predefined_widgets) return;
  let num_added = 0;
  let num_changed = 0;
  let num_unchanged = 0;
  if (!props.doReplace) {
    console.log('Replacing widgets - old:', predefined_widgets, 'new:', new_predefined_widgets);
    // Now we want to update the predefined_widgets
    new_predefined_widgets.forEach((predefined_widget) => {
      const predefined_widget_index = predefined_widgets.findIndex(
        (w) => w.id === predefined_widget.id || (props.doMerge && w.name === predefined_widget.name)
      );
      if (predefined_widget_index === -1) {
        // if it doesn't exist, add the new widget
        if (!props.doMerge) {
          predefined_widgets.push(predefined_widget);
        } else {
          console.warn('Failed to find widget to merge:', predefined_widget?.name);
        }
        num_added++;
      } else {
        // otherwise replace it
        const new_predefined_widget = !props.doMerge
          ? predefined_widget
          : {
              ...predefined_widgets[predefined_widget_index],
              ...predefined_widget,
            };
        if (JSON.stringify(predefined_widgets[predefined_widget_index]) !== JSON.stringify(new_predefined_widget)) {
          num_changed++;
          predefined_widgets[predefined_widget_index] = new_predefined_widget;
        } else {
          num_unchanged++;
        }
      }
    });
  } else {
    // Just count
    num_added = predefined_widgets.length;
  }
  // And re-sort the list by name
  predefined_widgets.sort((a, b) => a.name.localeCompare(b.name));
  if (!props.doDryRun) {
    await thunkAPI.dispatch(updatePredefinedWidgetsThunk({ predefined_widgets })).unwrap();
  }
  // Note that updatePredefinedWidgetsThunk will handle updating the store, so no need to do it here
  return { num_added, num_changed, num_unchanged };
});

export const deletePredefinedWidgetThunk = createAsyncThunk<
  void, // return type
  string, // params - id of widget
  { state: { fhir: FhirState; user: UserState } }
>('fhir/deletePredefinedWidget', async (predefined_widget_id, thunkAPI) => {
  // We want to update just a single widget.  We do this in a very crude way.
  // First, refetch the latest list of widgets, just incase someone else editted them.
  // Then modify this specific widget, and then update the entire of widgets.
  const orgId = thunkAPI.getState().user.pearlUserInfo?.orgId;
  const predefined_widgets = (await fetchPredefinedWidgets(orgId))?.predefined_widgets;
  if (!predefined_widgets) return;
  const predefined_widget = predefined_widgets.find((w) => w.id === predefined_widget_id);
  if (predefined_widget) {
    predefined_widget.deleted = true;
  }
  // And re-sort the list by name
  predefined_widgets.sort((a, b) => a.name.localeCompare(b.name));
  await thunkAPI.dispatch(updatePredefinedWidgetsThunk({ predefined_widgets })).unwrap();
  // Note that updatePredefinedWidgetsThunk will handle updating the store, so no need to do it here
});

export const updatePredefinedWidgetsThunk = createAsyncThunk<
  | {
      predefined_widgets?: FhirPredefinedWidget[];
      groupedObservations?: GroupedObservationType[];
    }
  | undefined, // return type
  Partial<{
    predefined_widgets?: FhirPredefinedWidget[];
    groupedObservations?: GroupedObservationType[];
  }>, // params  - will only update the ones passed
  { state: { fhir: FhirState; user: UserState } }
>('fhir/updatePredefinedWidgets', async (newData, thunkAPI) => {
  const pearlUserInfo = thunkAPI.getState().user.pearlUserInfo;
  if (!pearlUserInfo?.userFlags?.edit_predefined_widgets)
    throw new Error('You do not have the edit_predefined_widgets permission');
  const orgId = thunkAPI.getState().user.pearlUserInfo?.orgId;
  if (!orgId) {
    throw new Error('You do not have an orgId?');
  }
  // Get the latest value first, so we can update it
  const latestData = await pearlApi.get<{ data: FhirNetworkOrganizationValuesFhirType }>(
    `/organizations/${orgId}/values/fhir`
  );
  delete (latestData.data.data as any).data;
  if (latestData.data.data.predefined_widgets === undefined && newData.predefined_widgets === undefined) {
    throw new Error('Failed to update widgets');
  }

  const widgetsToSave = newData.predefined_widgets?.map(convertFhirPredefinedWidgetToFhirNetworkPredefinedWidget);
  if (widgetsToSave) {
    const savedIds = new Set(widgetsToSave.map((widget) => fixupWidgetId(widget.id)));
    // Deprecated records must survive replacement imports so existing topic references still resolve.
    latestData.data.data.predefined_widgets?.forEach((widget) => {
      if (widget.deleted && !savedIds.has(fixupWidgetId(widget.id))) {
        widgetsToSave.push(widget);
      }
    });
  }
  const data: FhirNetworkOrganizationValuesFhirType = {
    ...latestData.data.data,
    predefined_widgets: widgetsToSave ?? latestData.data.data.predefined_widgets,
    groupedObservations: newData.groupedObservations || latestData.data.data.groupedObservations,
  };

  try {
    await pearlApi.put<{ data: FhirNetworkOrganizationValuesFhirType }>(`/organizations/${orgId}/values/fhir`, {
      data,
    });
  } catch (error) {
    throw new Error('Failed to update widgets');
  }
  return {
    predefined_widgets: data.predefined_widgets?.map(convertFhirNetworkPredefinedWidgetToFhirPredefinedWidget),
    groupedObservations: data.groupedObservations,
  };
});

/** Dispatch mergeGroupedObservations() then updatePredefinedWidgetsThunk to save it */
export const mergeGroupedObservationsAndSaveThunk = createAsyncThunk<
  void, // return type
  string[], // params - ids of widgets to merge together
  { state: { fhir: FhirState; user: UserState } }
>('fhir/mergeGroupedObservationsAndSave', async (groupedObservations, thunkAPI) => {
  thunkAPI.dispatch(mergeGroupedObservations(groupedObservations));
  // This should be immediately updated
  await thunkAPI
    .dispatch(
      updatePredefinedWidgetsThunk({
        groupedObservations: JSON.parse(JSON.stringify(thunkAPI.getState().fhir.groupedObservations)),
      })
    )
    .unwrap();
});

/** Dispatch unmergeGroupedObservations() to split the selected observation from its group then updatePredefinedWidgetsThunk to save it */
export const unmergeGroupedObservationsAndSaveThunk = createAsyncThunk<
  void, // return type
  string, // params - id of widget to un-merge  (split) together
  { state: { fhir: FhirState; user: UserState } }
>('fhir/unmergeGroupedObservationsAndSave', async (groupedObservations, thunkAPI) => {
  thunkAPI.dispatch(unmergeGroupedObservations(groupedObservations));
  // This should be immediately updated
  await thunkAPI
    .dispatch(
      updatePredefinedWidgetsThunk({
        groupedObservations: JSON.parse(JSON.stringify(thunkAPI.getState().fhir.groupedObservations)),
      })
    )
    .unwrap();
});

let fetchFhirPatientDataAbortController = new AbortController();

/** Called by the FhirPatientComponent */
export const fetchFhirPatientThunk = createAsyncThunk<
  | {
      patient: IPatient | undefined;
      server: string | undefined;
      mrn: string | undefined;
      diagnosticReports?: IDiagnosticReportSimplified[];
      medicines?: IMedicationRequest[];
      vitals?: IObservation[];
      problemList?: ICondition[];
      medicalHx?: ICondition[];
      surgicalHx?: IProcedure[];
    }
  | undefined, // return
  PatientContextType, // params
  { state: RootState }
>('fhir/fetchFhirPatient', async (fhirPatient, thunkAPI) => {
  // Abort any previous fetchFhirPatientThunk calls
  fetchFhirPatientDataAbortController.abort();
  throwIfAborted(thunkAPI.signal);

  if (fhirPatient.azureFilename) {
    return await fetchFhirPatientFromAzure(fhirPatient, thunkAPI.signal);
  }

  const patient = fhirPatient.fhirPatientId
    ? await fetchPatient(fhirPatient.fhirPatientId, thunkAPI.signal, fhirPatient.server)
    : fhirPatient.mrn
    ? await fetchPatientByMRN(fhirPatient.mrn, thunkAPI.signal)
    : undefined;
  // Check if the thunk has been cancelled
  throwIfAborted(thunkAPI.signal);

  const mrn = fhirPatient.mrn || (patient && getPatientMRN(patient)?.value);
  const server = patient?.server || fhirPatient.server;
  // console.log('Patient: ', patient);
  void thunkAPI.dispatch(fetchFhirPatientDataThunk({ fhirPatientId: patient?.id, mrn, server }));
  // Check if the thunk has been cancelled
  throwIfAborted(thunkAPI.signal);
  return { patient, server, mrn };
});

/** This function is called by fetchFhirPatientThunk()
 *
 *  This is the main function that fetches all the FHIR data for a patient.
 *  It fetches:
 *    - diagnostic reports (with observatinos)
 *    - medicines
 *    - vitals
 *    - problem list
 *    - medical history
 *    - surgical history
 *    - care everywhere
 *
 *  Note that this function isn't called for test patients from Azure - @see fetchFhirPatientFromAzure()
 *
 */
const fetchFhirPatientDataThunk = createAsyncThunk<
  undefined,
  | {
      fhirPatientId: string | undefined;
      mrn: string | undefined;
      server: string | undefined;
      fhirConfig?: FhirWidgetBase | undefined; // If passed in, it will fetch only these diagnostic reports
    }
  | undefined, // undefined to allow us to cancel any ongoing fetchFhirPatientDataThunk call
  { state: { fhir: FhirState; user: UserState } }
>('fhir/fetchFhirPatientData', async (args, thunkAPI) => {
  if (!args) return;
  const { fhirPatientId, mrn, server, fhirConfig } = args;
  if (!fhirPatientId || !mrn || !server) {
    return undefined;
  }

  // combine the two abort signals
  fetchFhirPatientDataAbortController = new AbortController();
  thunkAPI.signal.addEventListener('abort', () => {
    fetchFhirPatientDataAbortController.abort();
  });
  const signal = fetchFhirPatientDataAbortController.signal;

  try {
    throwIfAborted(signal);
    const testAndSetLoading = (cptOrName: string) => {
      const is_loading = thunkAPI.getState().fhir.observationCptOrNameLoadingOrLoaded[cptOrName];
      if (!is_loading) {
        thunkAPI.dispatch(setObservationCptOrNameLoadingOrLoaded(cptOrName));
        return true;
      }
      return false;
    };

    const diagnosticReportsOnlyRequiredPromise = fetchDiagnosticReportsOnlyRequired(
      fhirPatientId,
      mrn,
      server,
      signal,
      fhirConfig,
      testAndSetLoading, // This is a callback that says if are already loading this cptOrName. If not, then set it to loading and return true, else false
      (diagnosticReports) => {
        // This is a callback to provide update as we fetch the data, just to speed up the UI
        thunkAPI.dispatch(
          updateDiagnosticReportsPartial({
            diagnosticReports,
            medicines: undefined,
            vitals: undefined,
            problemList: undefined,
            fhirPatientId,
            medicalHx: undefined,
            surgicalHx: undefined,
          })
        );
      }
    );
    const medicinesVitalsPromise = fetchMedicinesVitals(fhirPatientId, mrn, server, signal, (medicines, vitals) => {
      // This is a callback to provide update as we fetch the data, just to speed up the UI
      // Will be called multiple times, first with medicines, then with vitals
      thunkAPI.dispatch(
        updateDiagnosticReportsPartial({
          diagnosticReports: undefined,
          medicines,
          vitals,
          problemList: undefined,
          medicalHx: undefined,
          fhirPatientId,
          surgicalHx: undefined,
        })
      );
    });
    const problemListPromise = fetchProblemList(fhirPatientId, mrn, server, signal, (problemList) => {
      // This is a callback to provide update as we fetch the data, just to speed up the UI
      // Will be called multiple times, first with medicines, then with vitals
      thunkAPI.dispatch(
        updateDiagnosticReportsPartial({
          diagnosticReports: undefined,
          medicines: undefined,
          medicalHx: undefined,
          vitals: undefined,
          problemList,
          fhirPatientId,
          surgicalHx: undefined,
        })
      );
    });
    // const medicalHxPromise = fetchPatientMedicalHx(fhirPatientId, mrn, server, signal, (medicalHx) => {
    //   // This is a callback to provide update as we fetch the data
    //   thunkAPI.dispatch(
    //     updateDiagnosticReportsPartial({
    //       diagnosticReports: undefined,
    //       medicines: undefined,
    //       vitals: undefined,
    //       problemList: undefined,
    //       fhirPatientId,
    //       medicalHx,
    //       surgicalHx: undefined,
    //     })
    //   );
    // });
    const surgicalHxPromise = fetchPatientSurgicalHx(fhirPatientId, mrn, server, signal, (surgicalHx) => {
      // This is a callback to provide update as we fetch the data
      thunkAPI.dispatch(
        updateDiagnosticReportsPartial({
          diagnosticReports: undefined,
          medicines: undefined,
          vitals: undefined,
          problemList: undefined,
          fhirPatientId,
          medicalHx: undefined,
          surgicalHx: surgicalHx,
        })
      );
    });
    const careEverywherePromise = shouldFetchCareEverywhere()
      ? fetchCareEverywhere(fhirPatientId, false, mrn, server, signal, (careEverywhereData) => {
          thunkAPI.dispatch(updateCareEverywhereData({ careEverywhereData, fhirPatientId }));
          analyticsTrackCareEverywhereData({
            careEverywhereData,
            pearlUserInfo: thunkAPI.getState().user.pearlUserInfo!,
          });
        })
      : Promise.resolve();
    /*console.log(
      'This patient has:',
      diagnosticReports?.map((r) => `${r.code?.text}: ${getCpt4Code(r?.code?.coding)}`)
    );*/
    const patientDataPromises = [diagnosticReportsOnlyRequiredPromise, medicinesVitalsPromise, careEverywherePromise];
    if (showPatientProblemListAndPastMedicalHx()) {
      // patientDataPromises.push(problemListPromise, medicalHxPromise, surgicalHxPromise);
      patientDataPromises.push(problemListPromise, surgicalHxPromise);
    }
    // Wait for all in parallel
    await Promise.all(patientDataPromises);
    return undefined;
  } catch (error_) {
    const error = error_ as Error;
    // 404 means the document doesn't have a published version
    // Check it's not just a cancelled request
    if (error.name === 'AbortError' || axios.isCancel(error)) {
      return;
    }
    snackNotifications.error(`Failed to fetch patient data: ${(error as Error).message}`);
    console.error(error);
    throw error;
  }
});

export const fetchKnownAcronymDataThunk = createAsyncThunk<
  Record<string, string>,
  void,
  { state: { fhir: FhirState; user: UserState } }
>('fhir/fetchFhirAcronymData', async () => {
  const res = await azureDownloadKnownAcronyms();
  return JSON.parse(res);
});

export const fhirSlice = createSlice({
  name: 'fhir',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setPatientContext: (state, action: PayloadAction<PatientContextType | undefined>) => {
      state.patientContext = action.payload;
      state.fhirPatientData = {
        diagnosticReports: undefined,
        medicines: undefined,
        vitals: undefined,
        problemList: undefined,
        medicalHx: undefined,
        surgicalHx: undefined,
      };
    },
    removeListOfHtmlFhir: (state, action: PayloadAction<{ id: string }>) => {
      delete state.listOfHtmlFhirById[action.payload.id];
    },
    setListOfHtmlFhir: (state, action: PayloadAction<{ id: string; listOfHtmlFhir: HtmlFhirInstance[] }>) => {
      const { id, listOfHtmlFhir } = action.payload;
      if (JSON.stringify(state.listOfHtmlFhirById[id]) !== JSON.stringify(listOfHtmlFhir)) {
        state.listOfHtmlFhirById[id]! = listOfHtmlFhir;
      }
    },
    /** We are either hovering over a word (<fhir>) which has one fhirWidgetId (the id on the <fhir>) and
     *  will be mapped to 0 or more predefinedWidgetIds, and hoveringOverWidget=false
     * OR
     *  hovering over a predefined widget in the sidebar, which will have zero or one predefinedWidgetIds,
     *  and one or more fhirWidgetIds (the ids on the <fhir> tags in the document which map to this predefinedWidgetId)
     *  and hoveringOverWidget=true
     */
    setFhirHighlight: (
      state,
      action: PayloadAction<
        | {
            messageIndex: number;
            fhirWidgetIds: string[];
            predefinedWidgetIds: string[] | undefined; // undefined or empty means not configured yet
            hoveringOverWidget: boolean;
            documentVersionId: string | undefined;
            pageId: string | undefined;
            inEditorMode: boolean; // If true, then we we have CKEditor open and don't hide or collapse the sidebar widgets
          }
        | undefined
      >
    ) => {
      if (!action.payload) {
        state.highlightFhir = undefined;
        return;
      }
      // Remove any .1 etc that we have in the message index which comes from having multiple pages in a message
      action.payload.messageIndex = Math.floor(action.payload.messageIndex);

      const { fhirWidgetIds, documentVersionId, pageId, predefinedWidgetIds, messageIndex } = action.payload;

      if (!fhirWidgetIds?.[0]) {
        console.error('Internal error: fhirWidgetId is undefined');
        return;
      }

      const rightPanelWidgetIds = (predefinedWidgetIds?.length ? predefinedWidgetIds : [undefined]).map(
        (predefinedWidgetId) =>
          fhirWidgetIdToRightPanelWidgetId(
            fhirWidgetIds[0]!,
            predefinedWidgetId,
            messageIndex,
            !!action.payload?.inEditorMode
          )
      );
      const highlightFhir: FhirState['highlightFhir'] = {
        messageIndex: action.payload.messageIndex,
        hoveringOverWidget: action.payload.hoveringOverWidget,
        fhirWidgetIds,
        predefinedWidgetIds: predefinedWidgetIds?.length ? predefinedWidgetIds : undefined,
        rightPanelWidgetIds,
        documentVersionId: documentVersionId!,
        pageId: pageId!,
      };

      // Don't update if it's the same to avoid possible recursive rendering loops
      if (JSON.stringify(state.highlightFhir) !== JSON.stringify(highlightFhir)) {
        state.highlightFhir = highlightFhir;
      }
    },
    fhirHighlightForceUpdate: (state) => {
      if (state.highlightFhir) {
        // This is a hack to force a re-render of the FhirHighlight component
        state.highlightFhir = { ...state.highlightFhir };
      }
    },
    setCurrentlyHighlightedTop: (state, action: PayloadAction<number | undefined>) => {
      state.highlightFhirTop = action.payload;
    },
    setActiveFhirSidebarMessageIndex: (state, action: PayloadAction<number | undefined>) => {
      state.activeSidebarMessageIndex = action.payload == null ? undefined : Math.floor(action.payload);
    },
    setIsFhirVisible: (state, action: PayloadAction<boolean>) => {
      state.isFhirVisible = action.payload;
    },
    setIsFhirFilterVisible: (state, action: PayloadAction<boolean | undefined>) => {
      state.isFhirFilterVisible = action.payload;
    },
    setObservationCptOrNameLoadingOrLoaded: (state, action: PayloadAction<string>) => {
      state.observationCptOrNameLoadingOrLoaded[action.payload] = true;
    },
    setDateRange: (state, action: PayloadAction<number>) => {
      state.dateRange = action.payload;
    },
    updateDiagnosticReports: (
      state,
      action: PayloadAction<{
        diagnosticReports: IDiagnosticReportSimplified[] | undefined;
        medicines: IMedicationRequest[] | undefined;
        vitals: IObservation[] | undefined;
        problemList: ICondition[] | undefined;
        fhirPatientId: string;
        medicalHx: ICondition[] | undefined;
        surgicalHx: IProcedure[] | undefined;
      }>
    ) => {
      if (
        action.payload &&
        (!state.patientContext?.fhirPatientId || action.payload?.fhirPatientId === state.patientContext?.fhirPatientId)
      ) {
        state.fhirPatientData = {
          diagnosticReports: action.payload.diagnosticReports,
          medicines: action.payload.medicines,
          vitals: action.payload.vitals,
          problemList: action.payload.problemList,
          medicalHx: action.payload.medicalHx,
          surgicalHx: action.payload.surgicalHx,
        };
      }
    },
    /** For the admin page - merges two group observations together
     * See mergeGroupedObservationsAndSaveThunk which calls this and then saves
     */
    mergeGroupedObservations: (state, action: PayloadAction<string[]>) => {
      const setIds = new Set(action.payload);
      if (!state.groupedObservations) return;
      // We need to do this in two passes. First go through all the groupedObservations and add the mergeWith to the setIds
      state.groupedObservations.forEach((groupedObservation) => {
        if (setIds.has(groupedObservation.id) && groupedObservation.mergeWith) {
          groupedObservation.mergeWith.forEach((id) => setIds.add(id));
        }
      });
      // Now go through all the groupedObservations again and add the setIds to the mergeWith
      state.groupedObservations.forEach((groupedObservation) => {
        if (setIds.has(groupedObservation.id)) {
          groupedObservation.mergeWith = Array.from(setIds);
        }
      });
    },
    /** For the admin page - un-merges (splits) the observation from its group. */
    unmergeGroupedObservations: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (!state.groupedObservations) return;
      state.groupedObservations.forEach((groupedObservation) => {
        if (groupedObservation.mergeWith && groupedObservation.mergeWith.includes(id)) {
          groupedObservation.mergeWith = groupedObservation.mergeWith.filter((i) => i !== id);
        }
        if (groupedObservation.id === id) {
          groupedObservation.mergeWith = undefined;
        }
      });
    },
    updateCareEverywhereData: (
      state,
      action: PayloadAction<{
        careEverywhereData: CareEverywherePanel[] | undefined;
        fhirPatientId: string | undefined;
      }>
    ) => {
      if (
        action.payload &&
        state.patientContext?.fhirPatientId &&
        action.payload?.fhirPatientId === state.patientContext?.fhirPatientId
      ) {
        state.fhirPatientData.careEverywhere = action.payload.careEverywhereData;
      }
    },
    updateDiagnosticReportsPartial: (
      state,
      action: PayloadAction<{
        diagnosticReports: IDiagnosticReportSimplified[] | undefined;
        medicines: IMedicationRequest[] | undefined;
        vitals: IObservation[] | undefined;
        problemList: ICondition[] | undefined;
        fhirPatientId: string;
        medicalHx: ICondition[] | undefined;
        surgicalHx: IProcedure[] | undefined;
      }>
    ) => {
      if (
        action.payload &&
        (!state.patientContext?.fhirPatientId || action.payload?.fhirPatientId === state.patientContext?.fhirPatientId)
      ) {
        if (action.payload.diagnosticReports) {
          state.fhirPatientData.diagnosticReports = state.fhirPatientData?.diagnosticReports
            ? [...state.fhirPatientData.diagnosticReports!, ...action.payload.diagnosticReports]
            : action.payload.diagnosticReports;
        }
        if (action.payload.medicines) {
          state.fhirPatientData.medicines = state.fhirPatientData?.medicines
            ? [...state.fhirPatientData.medicines!, ...action.payload.medicines]
            : action.payload.medicines;
        }
        if (action.payload.vitals) {
          state.fhirPatientData.vitals = state.fhirPatientData?.vitals
            ? [...state.fhirPatientData.vitals!, ...action.payload.vitals]
            : action.payload.vitals;
        }
        if (action.payload.problemList) {
          state.fhirPatientData.problemList = state.fhirPatientData?.problemList
            ? [...state.fhirPatientData.problemList!, ...action.payload.problemList]
            : action.payload.problemList;
        }
        if (action.payload.medicalHx) {
          state.fhirPatientData.medicalHx = state.fhirPatientData?.medicalHx
            ? [...state.fhirPatientData.medicalHx!, ...action.payload.medicalHx]
            : action.payload.medicalHx;
        }
        if (action.payload.surgicalHx) {
          state.fhirPatientData.surgicalHx = state.fhirPatientData?.surgicalHx
            ? [...state.fhirPatientData.surgicalHx!, ...action.payload.surgicalHx]
            : action.payload.surgicalHx;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFhirPatientThunk.pending, (state, action) => {
        if (!state.patientContext) {
          return; // should be impossible?  Since we fetched this patient
        }
        state.patientContext!.patient = undefined;
        state.patientContext!.server = undefined;
        state.loadingPatient = true;
        console.log('fetchFhirPatientThunk.pending clearing patient data');
        state.fhirPatientData = {
          diagnosticReports: undefined,
          medicines: undefined,
          vitals: undefined,
          problemList: undefined,
          medicalHx: undefined,
          surgicalHx: undefined,
        };
        state.observationCptOrNameLoadingOrLoaded = {};
        state.diagnosticReportObservationsLoading = {};
        state.patientContext.mrn = action.meta.arg.mrn;
        state.patientContext.server = action.meta.arg.server;
      })
      .addCase(fetchFhirPatientThunk.fulfilled, (state, action) => {
        state.loadingPatient = false;
        if (!state.patientContext) {
          return; // should be impossible?  Since we fetched this patient
        }
        if (!action.payload) {
          state.patientContext = undefined;
          return;
        }
        state.patientContext.patient = action.payload.patient;
        state.patientContext.fhirPatientId = action.payload?.patient?.id;
        state.patientContext.mrn = action.payload?.mrn;
        state.patientContext.server = action.payload.server;
        state.fhirPatientData = {
          diagnosticReports: action.payload.diagnosticReports,
          medicines: action.payload.medicines,
          vitals: action.payload.vitals,
          problemList: action.payload.problemList,
          medicalHx: action.payload.medicalHx,
          surgicalHx: action.payload.surgicalHx,
        };
        // console.log('id is', state.patientContext!.fhirPatientId);
      })
      .addCase(fetchFhirPatientThunk.rejected, (state) => {
        state.loadingPatient = false;
      })
      .addCase(fetchFhirPatientDataThunk.pending, (state) => {
        state.loadingObservations = true;
        console.log('fetchFhirPatientDataThunk.pending clearing patient data');
        state.fhirPatientData = {
          diagnosticReports: undefined,
          medicines: undefined,
          vitals: undefined,
          problemList: undefined,
          medicalHx: undefined,
          surgicalHx: undefined,
        };
      })
      .addCase(fetchFhirPatientDataThunk.fulfilled, (state, action) => {
        state.loadingObservations = false;
      })
      .addCase(fetchFhirPatientDataThunk.rejected, (state) => {
        state.loadingObservations = false;
      })
      .addCase(fetchObservationsForDiagnosticReportThunk.pending, (state, action) => {
        const { diagnostic_report_id } = action.meta.arg;
        state.diagnosticReportObservationsLoading[diagnostic_report_id!] = true;
      })
      .addCase(fetchObservationsForDiagnosticReportThunk.rejected, (state, action) => {
        const { diagnostic_report_id } = action.meta.arg;
        delete state.diagnosticReportObservationsLoading[diagnostic_report_id!];
      })
      .addCase(fetchObservationsForDiagnosticReportThunk.fulfilled, (state, action) => {
        const { diagnostic_report_id } = action.meta.arg;
        delete state.diagnosticReportObservationsLoading[diagnostic_report_id!];
        if (!state.patientContext || !action.payload || state.patientContext.mrn !== action.payload?.mrn) {
          return; // Some sanity checking
        }
        const diagnosticReportWithObservations = action.payload.diagnosticReportWithObservations;
        if (!diagnosticReportWithObservations) {
          console.warn('Could not load diagnosticReportWithObservations for reference: ', action.meta.arg);
          return;
        }
        // We have fetched an diagnosticReportWithObservations, update every reference to it
        if (diagnosticReportWithObservations) {
          updatePatientDataFromDiagnosticReport(
            state.fhirPatientData.diagnosticReports,
            diagnosticReportWithObservations
          );
        }
      })
      .addCase(fetchPredefinedWidgetsThunk.pending, (state) => {
        state.loadingPredefinedWidgets = true;
      })
      .addCase(fetchPredefinedWidgetsThunk.fulfilled, (state, action) => {
        if (action.payload) {
          const { predefined_widgets, groupedObservations } = action.payload;
          // Sort them by name, to show them in a nice order
          state.predefinedWidgets = predefined_widgets?.sort((a, b) => a.name?.localeCompare(b.name)) || [];
          state.groupedObservations = groupedObservations;
        }
        state.loadingPredefinedWidgets = false;
      })
      .addCase(fetchPredefinedWidgetsThunk.rejected, (state) => {
        state.loadingPredefinedWidgets = false;
      })
      .addCase(updatePredefinedWidgetsThunk.fulfilled, (state, action) => {
        if (action.payload) {
          state.predefinedWidgets = action.payload.predefined_widgets;
          state.groupedObservations = action.payload.groupedObservations;
        }
      })
      .addCase(fetchKnownAcronymDataThunk.pending, (state) => {
        state.loadingKnownAcronyms = true;
      })
      .addCase(fetchKnownAcronymDataThunk.rejected, (state) => {
        state.loadingKnownAcronyms = true;
      })
      .addCase(fetchKnownAcronymDataThunk.fulfilled, (state, action) => {
        state.loadingKnownAcronyms = true;
        state.knownAcronyms = action.payload;
      });
  },
});

export const {
  setPatientContext,
  removeListOfHtmlFhir,
  setFhirHighlight,
  fhirHighlightForceUpdate,
  setCurrentlyHighlightedTop,
  setActiveFhirSidebarMessageIndex,
  setListOfHtmlFhir,
  updateDiagnosticReports,
  updateDiagnosticReportsPartial,
  updateCareEverywhereData,
  setObservationCptOrNameLoadingOrLoaded,
  setIsFhirVisible,
  setDateRange,
  setIsFhirFilterVisible,
  mergeGroupedObservations,
  unmergeGroupedObservations,
} = fhirSlice.actions;

export const selectIsFhirVisible = (state: RootState) => state.fhir.isFhirVisible;
export const selectIsFhirFilterVisible = (state: RootState) => state.fhir.isFhirFilterVisible;
export const selectActiveFhirSidebarMessageIndex = (state: RootState) => state.fhir.activeSidebarMessageIndex;
export const selectFhirDiagnosticReports = (state: RootState) => state.fhir.fhirPatientData?.diagnosticReports;
export const selectFhirMedicines = (state: RootState) => state.fhir.fhirPatientData?.medicines;
export const selectFhirVitals = (state: RootState) => state.fhir.fhirPatientData?.vitals;
export const selectFhirProblemList = (state: RootState) => state.fhir.fhirPatientData?.problemList;
export const selectFhirMedicalHx = (state: RootState) => state.fhir.fhirPatientData?.medicalHx;
export const selectFhirSurgicalHx = (state: RootState) => state.fhir.fhirPatientData?.surgicalHx;
export const selectFhirCareEverywhere = (state: RootState) => state.fhir.fhirPatientData?.careEverywhere;
export const selectIsLoadingFhirPatientData = (state: RootState): boolean =>
  state.fhir.loadingPatient || state.fhir.loadingObservations;
export const selectHasFhirPatientData = (state: RootState): boolean =>
  !!state.fhir.fhirPatientData.diagnosticReports ||
  !!state.fhir.fhirPatientData.medicines ||
  !!state.fhir.fhirPatientData.vitals ||
  !!state.fhir.fhirPatientData.medicalHx ||
  !!state.fhir.fhirPatientData.problemList ||
  !!state.fhir.fhirPatientData.careEverywhere ||
  !!state.fhir.fhirPatientData.surgicalHx;
export const selectPatientContext = (state: RootState): PatientContextType | undefined => state.fhir.patientContext;
export const selectFromSmartOnFhir = (state: RootState): boolean | null =>
  state.fhir.patientContext?.fromSmartOnFhir || null;
export const selectFhirPatientId = (state: RootState): string | undefined => state.fhir.patientContext?.fhirPatientId;
export const selectFhirPatientMRN = (state: RootState): string | undefined => state.fhir.patientContext?.mrn;
export const selectFhirPatientServer = (state: RootState): string | undefined => state.fhir.patientContext?.server;
export const selectFhirHighlightTop = (state: RootState) => state.fhir.highlightFhirTop;

const selectFhirHighlightRaw_ = (state: RootState) => state.fhir.highlightFhir; // Don't use directly, use selectFhirHighlight
// Combine state.fhir.highlightFhir with state.chat
export const selectFhirHighlight = createSelector(
  [
    selectFhirHighlightRaw_,
    (state: RootState) => state.chat.documentVersions,
    (state: RootState) => state.chat.unsavedPages,
  ],
  (highlightFhir, documentVersions, unsavedPages) => {
    if (!highlightFhir) return undefined;
    const { fhirWidgetIds, documentVersionId, pageId, rightPanelWidgetIds, predefinedWidgetIds, hoveringOverWidget } =
      highlightFhir;
    const page = unsavedPages[pageId] || documentVersions[documentVersionId]?.pages[pageId];
    const callouts: {
      fhirWidgetId: string;
      predefinedWidgetId: string | undefined;
      rightPanelWidgetId: string | undefined;
      observationGroupId: string | undefined;
    }[] = [];
    const predefinedWidgetsHighlighted: {
      id?: string;
      predefinedWidgets:
        | {
            id: string;
            observationGroup?:
              | {
                  id: string;
                }
              | undefined;
          }[]
        | undefined;
    }[] = fhirWidgetIds.map((id) => ({
      id,
      predefinedWidgets: page?.fhir?.idsToFhir[id]?.predefinedWidgets.filter((pw) => {
        if (hoveringOverWidget && predefinedWidgetIds) {
          return predefinedWidgetIds.includes(pw.id);
        }
        return true;
      }),
    }));

    // We now have the predefinedWidgets for each fhirWidgetId (each <fhir> can point to multiple predefinedWidgets)
    // But this structure isn't that useful for callouts. Let's flatten it for callouts.
    predefinedWidgetsHighlighted.forEach((highlighted) => {
      const highlightedPredefinedWidgets = highlighted.predefinedWidgets || [];

      if (highlightedPredefinedWidgets.length > 0) {
        highlightedPredefinedWidgets.forEach((predefinedWidget) => {
          const rightPanelWidgetIndex = predefinedWidgetIds?.findIndex((id) => id === predefinedWidget.id) ?? -1;
          const rightPanelWidgetId =
            rightPanelWidgetIndex >= 0 ? rightPanelWidgetIds?.[rightPanelWidgetIndex] : rightPanelWidgetIds?.[0];

          callouts.push({
            fhirWidgetId: highlighted.id!,
            predefinedWidgetId: predefinedWidget.id,
            rightPanelWidgetId,
            observationGroupId: predefinedWidget.observationGroup?.id,
          });
        });
      }
    });

    if (callouts.length === 0) {
      // Fall back to the raw highlight relationships when page-based mapping is unavailable,
      // for example with grouped or embedded contextualized spans.
      fhirWidgetIds.forEach((fhirWidgetId) => {
        (rightPanelWidgetIds || []).forEach((rightPanelWidgetId, index) => {
          callouts.push({
            fhirWidgetId,
            predefinedWidgetId: predefinedWidgetIds?.[index],
            rightPanelWidgetId,
            observationGroupId: undefined,
          });
        });
      });
    }

    // fhirObservationGroupIds must be a 1-1 mapping with fhirWidgetIds
    // see FhirCallout for example where it uses the fhirWidgetIds index to get the fhirObservationGroupIds index

    return {
      ...highlightFhir,
      predefinedWidgetsHighlighted,
      callouts,
    };
  }
);

export const selectFhirHighlightForMessage = (state: RootState, messageIndex: number) =>
  state.fhir.highlightFhir?.messageIndex === messageIndex ? selectFhirHighlight(state) : undefined;
export const selectFhirHighlightForId = (
  state: RootState,
  fhirWidgetIds: string[] | undefined,
  predefinedWidgetIds: string[] | undefined
) => {
  const highlightFhir = selectFhirHighlight(state);
  return (fhirWidgetIds && highlightFhir?.fhirWidgetIds.some((id) => fhirWidgetIds.includes(id))) ||
    (predefinedWidgetIds &&
      highlightFhir?.predefinedWidgetsHighlighted.some((pw) =>
        pw.predefinedWidgets?.some((predefinedWidget) => predefinedWidgetIds.includes(predefinedWidget.id))
      ))
    ? highlightFhir
    : undefined;
};
export const selectFhirPatientMRNHashed = createSelector([selectFhirPatientMRN], (mrn) =>
  mrn ? hashCode(mrn) : undefined
);
export const selectListOfHtmlFhirById = createSelector(
  [(state: RootState) => state.fhir.listOfHtmlFhirById],
  (listOfHtmlFhirById) => Object.values(listOfHtmlFhirById).flat(1)
);
export const selectHasHtmlFhir = (state: RootState): boolean => Object.keys(state.fhir.listOfHtmlFhirById).length > 0;
export const selectPredefinedWidgets = (state: RootState): FhirPredefinedWidget[] | undefined =>
  state.fhir.predefinedWidgets;
export const selectGroupedObservations = (state: RootState): GroupedObservationType[] | undefined =>
  state.fhir.groupedObservations;
export const selectGroupedObservationsMap = createSelector([selectGroupedObservations], (groupedObservations) =>
  groupedObservations ? updateFromGroupedObservations(groupedObservations) : undefined
);
// Similiar to selectGroupedObservationsMap but returns just a single map by id
export const selectGroupedObservationsMapById = createSelector([selectGroupedObservations], (groupedObservations) =>
  // return as a map by Id
  groupedObservations ? listToIdDict(groupedObservations) : undefined
);
export const selectPredefinedWidgetsMap = createSelector([selectPredefinedWidgets], (predefined_widgets) =>
  predefined_widgets ? listToIdDict(predefined_widgets) : undefined
);
export const selectLoadingPredefinedWidgets = (state: RootState) => state.fhir.loadingPredefinedWidgets;
export const selectDateRange = (state: RootState) => state?.fhir?.dateRange;
export default fhirSlice.reducer;
export const selectIsPatientChartPaneVisible = createSelector(
  [selectFhirVitals, selectFhirMedicines, selectFhirProblemList, selectFhirMedicalHx, selectFhirSurgicalHx],
  (vitals, medicines, problemList, medicalHx, surgicalHx) => {
    const patientMedicationFlagEnabled = showPatientMedicines();
    return isPatientChartPaneVisible(
      patientMedicationFlagEnabled,
      vitals,
      medicines,
      problemList,
      medicalHx,
      surgicalHx
    );
  }
);
export const selectLoadingKnownAcronyms = (state: RootState) => state.fhir.loadingKnownAcronyms;
export const selectKnownAcronyms = (state: RootState) => state.fhir.knownAcronyms;
